import { z } from 'zod';
import { UsageLicenseClassSchema } from './usage.ts';

/**
 * Exam attempts of signed-in users (F6 AC-2.2, D-4): the body of `POST /api/attempts`, the rows
 * `GET /api/attempts` and `GET /api/attempts/:id` return, and the list query.
 *
 * Both exams produce an `AttemptInput`:
 *  - theory (`@dcs/theory-core` `scoreExam`): `score` = correct answers, `maxScore` = `total`,
 *    `failCode` = `CRITICAL_WRONG` | `BELOW_PASS` | null, `details.wrongQuestionIds` = `wrongIds`,
 *    `details.criticalWrongQuestionIds` = `criticalWrongIds`, `details.passScore`, `details.seed`;
 *  - sim (`apps/app` exam store `ExamResult`): `score`, `maxScore` = the rule table's `maxScore`,
 *    `failCode` = `reason` (the eliminating rule code) or a below-pass code, `details.penaltyCodes` =
 *    one code per penalty applied (a repeated penalty repeats the code), `details.passScore`.
 *
 * `idempotencyKey` is generated by the client once per finished exam (offline queue, T-2.2): posting
 * the same attempt again returns the stored one instead of inserting a second row.
 */

/** UTF-8 size of a string in bytes (what SQLite's `length(CAST(x AS BLOB))` measures). */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const next = text.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i++;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

/** Size in bytes of `value` once stored (`JSON.stringify`, UTF-8). */
export const jsonByteLength = (value: unknown): number => utf8ByteLength(JSON.stringify(value));

/** `attempts.details` cap (same as the DB CHECK, apps/api `ATTEMPT_DETAILS_MAX_BYTES`). */
export const ATTEMPT_DETAILS_MAX_BYTES = 2048;

/** A stable id or rule code (`q123`, `LANE_DEPARTURE`): never free text. */
export const AttemptCodeSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/, 'Expected an id or code');

const CodeListSchema = z.array(AttemptCodeSchema).max(100);

/**
 * `attempts.details`: codes and ids only, ≤ 2 KB of JSON (strict: an unknown key is a 422, so free
 * text cannot slip in). Additive: a new optional key is fine, renaming one is breaking. The admin
 * statistics (F7) read `wrongQuestionIds` and `penaltyCodes`.
 */
export const AttemptDetailsSchema = z
  .strictObject({
    /** Theory: stable ids of the questions answered wrongly or left unanswered, in exam order. */
    wrongQuestionIds: CodeListSchema.optional(),
    /** Theory: the wrong critical questions (a subset of `wrongQuestionIds`). */
    criticalWrongQuestionIds: CodeListSchema.optional(),
    /** Sim: one rule code per penalty applied (a repeated penalty repeats the code). */
    penaltyCodes: CodeListSchema.optional(),
    /** Pass threshold of the exam, on the same scale as `score`. */
    passScore: z.int().min(0).max(1000).optional(),
    /** Time from start to finish, in seconds. */
    durationSec: z.int().min(0).max(86_400).optional(),
    /** Theory: the exam seed, so the history can rebuild the same question set. */
    seed: AttemptCodeSchema.optional(),
  })
  .refine((d) => jsonByteLength(d) <= ATTEMPT_DETAILS_MAX_BYTES, {
    message: `details must be at most ${ATTEMPT_DETAILS_MAX_BYTES} bytes of JSON`,
  });
export type AttemptDetails = z.infer<typeof AttemptDetailsSchema>;

export const AttemptKindSchema = z.enum(['theory', 'sim']);
export type AttemptKind = z.infer<typeof AttemptKindSchema>;

/** Regulation set id (`tt12-2025`): a code shape, the content registry owns the list. */
export const AttemptRegulationIdSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,31}$/, 'Invalid regulation id');

/** Content version stamped on the result (`v1`, `v1.2`, a sim course version ...). */
export const AttemptContentVersionSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/, 'Invalid content version');

const UuidSchema = z
  .string()
  .toLowerCase()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
    'idempotencyKey must be a UUID',
  );

/**
 * `POST /api/attempts` body. `finishedAt` is the client's clock (ISO 8601); the server stores it as
 * the attempt time (history order, statistics), capped at the server's "now" so a fast clock cannot
 * pin an attempt to the top of the list. Whole result in one object, no partial writes.
 */
export const AttemptInputSchema = z
  .strictObject({
    idempotencyKey: UuidSchema,
    kind: AttemptKindSchema,
    licenseClass: UsageLicenseClassSchema,
    regulationId: AttemptRegulationIdSchema,
    contentVersion: AttemptContentVersionSchema,
    score: z.int().min(0).max(1000),
    maxScore: z.int().min(1).max(1000),
    passed: z.boolean(),
    /** Why the exam failed; `null` when passed (theory `CRITICAL_WRONG` | `BELOW_PASS`, sim rule codes). */
    failCode: AttemptCodeSchema.nullable(),
    details: AttemptDetailsSchema,
    finishedAt: z.iso.datetime({ offset: true }),
  })
  .refine((a) => a.score <= a.maxScore, {
    message: 'score must not exceed maxScore',
    path: ['score'],
  })
  .refine((a) => !a.passed || a.failCode === null, {
    message: 'a passed attempt has no failCode',
    path: ['failCode'],
  });
export type AttemptInput = z.infer<typeof AttemptInputSchema>;

/** One stored attempt, as the API returns it. */
export const AttemptOutSchema = z.strictObject({
  id: z.string().min(1),
  idempotencyKey: z.string().min(1),
  kind: AttemptKindSchema,
  licenseClass: z.string().min(1),
  regulationId: z.string().min(1),
  contentVersion: z.string().min(1),
  score: z.int(),
  maxScore: z.int(),
  passed: z.boolean(),
  failCode: z.string().nullable(),
  details: AttemptDetailsSchema,
  /** ISO 8601 UTC: the `finishedAt` sent (capped at the server time it was stored). */
  finishedAt: z.iso.datetime(),
});
export type AttemptOut = z.infer<typeof AttemptOutSchema>;

export const ATTEMPTS_PAGE_DEFAULT = 20;
export const ATTEMPTS_PAGE_MAX = 50;

/** Opaque keyset cursor of `GET /api/attempts` (`<epoch ms>.<id>` of the last item of a page). */
export const AttemptCursorSchema = z
  .string()
  .regex(/^\d{1,15}\.[A-Za-z0-9-]{1,64}$/, 'Invalid cursor');

/** `GET /api/attempts?kind=&licenseClass=&cursor=&limit=` (newest first). */
export const AttemptListQuerySchema = z.strictObject({
  kind: AttemptKindSchema.optional(),
  licenseClass: UsageLicenseClassSchema.optional(),
  cursor: AttemptCursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(ATTEMPTS_PAGE_MAX).default(ATTEMPTS_PAGE_DEFAULT),
});
export type AttemptListQuery = z.infer<typeof AttemptListQuerySchema>;

/** One page: `nextCursor` is `null` on the last page. */
export const AttemptPageSchema = z.strictObject({
  items: z.array(AttemptOutSchema),
  nextCursor: AttemptCursorSchema.nullable(),
});
export type AttemptPage = z.infer<typeof AttemptPageSchema>;
